import { Capacitor, registerPlugin } from "@capacitor/core"
import type { MeResponse } from "./types"

const apiBase = import.meta.env.VITE_API_BASE || "/api"
const sessionKey = "joowfak.authSession"

export interface AuthUser {
  id: string
  email?: string
  emailVerified?: boolean
  displayName?: string
  avatarUrl?: string
  avatarPreset?: string
  publicCode?: string
  profileComplete?: boolean
}

export interface AuthSession {
  accessToken: string
  accessExpiresAt: string
  refreshToken: string
  refreshExpiresAt: string
  user: AuthUser
}

type GoogleCredentialResponse = { credential?: string }
type NativeAppleSignInResponse = { identityToken?: string; displayName?: string; email?: string }
export type AuthFlowErrorCode =
  | "apple_cancelled"
  | "apple_requires_2fa"
  | "apple_not_configured"
  | "apple_unavailable"
  | "apple_no_identity_token"
  | "apple_failed"

interface NativeAppleAuthPlugin {
  signIn(): Promise<NativeAppleSignInResponse>
  cancel?(): Promise<void>
}

export class AuthFlowError extends Error {
  code: AuthFlowErrorCode
  quiet: boolean

  constructor(code: AuthFlowErrorCode, message: string, quiet = false) {
    super(message)
    this.name = "AuthFlowError"
    this.code = code
    this.quiet = quiet
  }
}

const NativeAppleAuth = registerPlugin<NativeAppleAuthPlugin>("JoowFakAppleAuth")

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(input: { client_id: string; callback: (response: GoogleCredentialResponse) => void }): void
          renderButton(element: HTMLElement, options: Record<string, unknown>): void
        }
      }
    }
    AppleID?: {
      auth: {
        init(input: Record<string, unknown>): void
        signIn(): Promise<{ authorization?: { id_token?: string }; user?: { name?: { firstName?: string; lastName?: string } } }>
      }
    }
  }
}

export function currentAuthSession(): AuthSession | null {
  try {
    const raw = localStorage.getItem(sessionKey)
    return raw ? JSON.parse(raw) as AuthSession : null
  } catch {
    return null
  }
}

export function authAuthorizationHeader(): string | null {
  const session = currentAuthSession()
  return session?.accessToken ? `Bearer ${session.accessToken}` : null
}

export function storeAuthSession(session: AuthSession): void {
  localStorage.setItem(sessionKey, JSON.stringify(session))
}

export function clearAuthSession(): void {
  localStorage.removeItem(sessionKey)
}

export function isGoogleSignInConfigured(): boolean {
  return Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID || "")
}

export function isQuietAuthError(error: unknown): boolean {
  return error instanceof AuthFlowError && error.quiet
}

export async function refreshAuthSession(): Promise<AuthSession | null> {
  const session = currentAuthSession()
  if (!session?.refreshToken) return null
  const response = await fetch(`${apiBase}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ refreshToken: session.refreshToken }),
  })
  if (!response.ok) {
    clearAuthSession()
    return null
  }
  const next = await response.json() as AuthSession
  storeAuthSession(next)
  return next
}

export async function logout(): Promise<void> {
  const session = currentAuthSession()
  clearAuthSession()
  if (!session?.refreshToken) return
  try {
    await fetch(`${apiBase}/auth/logout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ refreshToken: session.refreshToken }),
    })
  } catch {
    // Local logout already happened.
  }
}

export async function deleteAccount(): Promise<void> {
  const authorization = authAuthorizationHeader()
  const response = await fetch(`${apiBase}/account`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: authorization ? { Authorization: authorization } : {},
  })
  clearAuthSession()
  if (!response.ok) throw new Error(await response.text())
}

export async function signInWithIdentityToken(provider: "apple" | "google", identityToken: string, displayName = "", avatarUrl = ""): Promise<AuthSession> {
  const response = await fetch(`${apiBase}/auth/${provider}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ identityToken, displayName, avatarUrl }),
  })
  if (!response.ok) throw await authResponseError(response)
  const session = await response.json() as AuthSession
  storeAuthSession(session)
  return session
}

export async function renderGoogleSignInButton(element: HTMLElement, onSession: (session: AuthSession) => void, onError: (error: Error) => void): Promise<void> {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || ""
  if (!clientId) throw new Error("Google client id is not configured")
  await loadScript("https://accounts.google.com/gsi/client", "google-identity-services")
  window.google?.accounts.id.initialize({
    client_id: clientId,
    callback(response) {
      if (!response.credential) {
        onError(new Error("Google did not return an identity token"))
        return
      }
      signInWithIdentityToken("google", response.credential).then(onSession).catch(onError)
    },
  })
  window.google?.accounts.id.renderButton(element, {
    theme: "outline",
    size: "large",
    type: "standard",
    width: Math.min(320, element.clientWidth || 320),
  })
}

export async function signInWithApple(): Promise<AuthSession> {
  if (Capacitor.getPlatform() === "ios") {
    let native: NativeAppleSignInResponse
    try {
      native = await withTimeout(
        NativeAppleAuth.signIn(),
        25_000,
        new AuthFlowError("apple_failed", "Sign in with Apple timed out. Try again."),
        () => void NativeAppleAuth.cancel?.(),
      )
    } catch (error) {
      throw normalizeAppleSignInError(error)
    }
    if (!native.identityToken) throw new AuthFlowError("apple_no_identity_token", "Apple did not return an identity token")
    return signInWithAppleIdentityToken(native.identityToken, native.displayName || "")
  }
  const clientId = import.meta.env.VITE_APPLE_CLIENT_ID || ""
  if (!clientId) throw new AuthFlowError("apple_not_configured", "Apple sign-in is not configured")
  await loadScript("https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js", "appleid-auth-js")
  window.AppleID?.auth.init({
    clientId,
    scope: "name email",
    redirectURI: import.meta.env.VITE_APPLE_REDIRECT_URI || window.location.origin,
    usePopup: true,
  })
  if (!window.AppleID?.auth.signIn) {
    throw new AuthFlowError("apple_unavailable", "Sign in with Apple is unavailable")
  }
  let result: Awaited<ReturnType<NonNullable<typeof window.AppleID>["auth"]["signIn"]>>
  try {
    result = await withTimeout(window.AppleID.auth.signIn(), 25_000, new AuthFlowError("apple_failed", "Sign in with Apple timed out. Try again."))
  } catch (error) {
    throw normalizeAppleSignInError(error)
  }
  const identityToken = result?.authorization?.id_token
  if (!identityToken) throw new AuthFlowError("apple_no_identity_token", "Apple did not return an identity token")
  const name = result?.user?.name
  const displayName = [name?.firstName, name?.lastName].filter(Boolean).join(" ")
  return signInWithAppleIdentityToken(identityToken, displayName)
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, error: Error, onTimeout?: () => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      onTimeout?.()
      reject(error)
    }, timeoutMs)
    promise.then((value) => {
      window.clearTimeout(timer)
      resolve(value)
    }, (reason) => {
      window.clearTimeout(timer)
      reject(reason)
    })
  })
}

async function signInWithAppleIdentityToken(identityToken: string, displayName = ""): Promise<AuthSession> {
  try {
    return await signInWithIdentityToken("apple", identityToken, displayName)
  } catch (error) {
    if (error instanceof AuthFlowError) throw error
    const message = meaningfulErrorText(error)
    throw new AuthFlowError("apple_failed", message || "Sign in with Apple did not complete. Try again.")
  }
}

function normalizeAppleSignInError(error: unknown): AuthFlowError {
  if (error instanceof AuthFlowError) return error
  const raw = meaningfulErrorText(error)
  const normalized = raw.toLowerCase()
  if (
    normalized.includes("cancel") ||
    normalized.includes("closed") ||
    normalized.includes("popup") ||
    normalized.includes("user_denied") ||
    normalized.includes("access_denied")
  ) {
    return new AuthFlowError("apple_cancelled", "Sign in with Apple was cancelled", true)
  }
  if (
    normalized.includes("two-factor") ||
    normalized.includes("two factor") ||
    normalized.includes("2fa") ||
    normalized.includes("multi-factor") ||
    normalized.includes("mfa")
  ) {
    return new AuthFlowError("apple_requires_2fa", "Sign in with Apple requires two-factor authentication")
  }
  return new AuthFlowError("apple_failed", raw || "Sign in with Apple did not complete")
}

function meaningfulErrorText(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === "string") return error
  if (!error || typeof error !== "object") return ""
  const record = error as Record<string, unknown>
  const parts = ["error", "code", "message", "reason", "details", "description"]
    .map((key) => record[key])
    .filter((value) => typeof value === "string" && value.trim())
    .map((value) => String(value).trim())
  if (parts.length) return parts.join(" ")
  try {
    const json = JSON.stringify(error)
    return json === "{}" ? "" : json
  } catch {
    return ""
  }
}

async function authResponseError(response: Response): Promise<Error> {
  const fallback = `Authentication request failed: ${response.status}`
  try {
    const body = await response.clone().json() as { error?: unknown }
    if (typeof body.error === "string" && body.error.trim()) return new Error(body.error.trim())
  } catch {
    // Fall through to text parsing.
  }
  try {
    const text = await response.text()
    return new Error(text.trim() || fallback)
  } catch {
    return new Error(fallback)
  }
}

export function userFromMe(me: MeResponse): AuthUser {
  return {
    id: me.subjectId.replace(/^user:/, ""),
    displayName: me.displayName,
    avatarUrl: me.avatarUrl,
    avatarPreset: me.avatarPreset,
    publicCode: me.publicCode,
    profileComplete: me.profileComplete,
  }
}

function loadScript(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve()
      return
    }
    const script = document.createElement("script")
    script.id = id
    script.src = src
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.head.appendChild(script)
  })
}
